import { Select, Table, TableColumnsType } from 'antd';
import moment from 'moment';
import QueryString from 'qs';
import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { useHistory, useLocation } from 'react-router-dom';
import { getEvents } from '../../../api/events.api';
import Button from '../../../components/Button';
import ErrorResponse from '../../../components/ErrorResponse';
import SimpleHeader from '../../../components/layout/SimpleHeader';
import Loader from '../../../components/Loader';
import Pagination from '../../../components/Pagination';
import SelectCampus from '../../../components/SelectCampus';
import useAuth from '../../../hooks/useAuth';
import useAxios from '../../../hooks/useAxios';
import config from '../../../lib/config';
import { IEventItem, IGetEvents } from '../../../models/events.model';
import { RootState } from '../../../store/rootReducer';
import useUtil from '../../../hooks/useUtil';

const Briefing = () => {
  const { t, i18n } = useTranslation();
  const { writeAuthYn } = useAuth();
  const history = useHistory();
  const location = useLocation();
  const { isCampusDeactived } = useUtil();
  const query = QueryString.parse(location.search, { ignoreQueryPrefix: true });
  const { auth, currentCampus } = useSelector((state: RootState) => state.auth);
  const { campusData } = useSelector((state: RootState) => state.campusData);
  const { data: res, isLoading, error, execute } = useAxios<IGetEvents>();

  const [campusCode, setCampusCode] = useState<string>();
  const [eventName, setEventName] = useState('');
  const [page, setPage] = useState<number>(1);
  const [param, setParam] = useState({});
  const [campusDeactived, setCampusDeactived] = useState(false);

  useEffect(() => {
    setCampusDeactived(isCampusDeactived());
  }, [isCampusDeactived]);

  useEffect(() => {
    if (auth && currentCampus) {
      const _param: any = {
        pageNo: page,
        perPage: config.pageSize,
      };

      if (query.page) {
        const _page = Number(query.page);
        setPage(_page);
        _param.page = _page;
      }
      if (query.eventName) {
        const _eventName = String(query.eventName);
        setEventName(_eventName);
        _param.eventName = _eventName;
      }
      if (auth.hqYn === 'N' && currentCampus) {
        _param.campusCode = currentCampus;
      } else if (auth.hqYn === 'Y' && query.campusCode) {
        const _campusCode = String(query.campusCode);
        setCampusCode(_campusCode);
        _param.campusCode = _campusCode;
      }

      execute(getEvents('PA', _param));
      _param.page = _param.pageNo;
      delete _param.pageNo;
      delete _param.perPage;
      setParam(_param);
    }
  }, [location, auth, currentCampus]);

  const onSearch = () => {
    const param: any = { page: 1 };

    if (auth.hqYn === 'Y' && campusCode) {
      param.campusCode = campusCode;
    } else if (auth.hqYn === 'N' && currentCampus) {
      param.campusCode = currentCampus;
    }
    if (eventName) {
      param.eventName = eventName;
    }

    history.push(`/event/briefing?${QueryString.stringify(param)}`);
  };

  const getCampusInfo = (record: IEventItem) => {
    if (record.allCampusYn === 'Y') {
      return t('common.text.all');
    } else {
      return record.campuses[0].campusKorName;
    }
  };

  const columns: TableColumnsType<IEventItem> = [
    {
      title: t('event.education.column1'),
      dataIndex: 'eventNo',
      key: 'eventNo',
      align: 'center',
    },
    {
      title: t('event.education.column2'),
      dataIndex: 'eventName',
      key: 'eventName',
      ellipsis: true,
      render: value => {
        return <div className="text-left ant-table-cell-ellipsis">{value}</div>
      }
    },
    {
      title: t('event.education.column3'),
      dataIndex: 'openYn',
      key: 'openYn',
      align: 'center',
    },
    {
      title: t('common.menu.campus'),
      dataIndex: 'allCampusYn',
      key: 'allCampusYn',
      align: 'center',
      render: (value, record) => getCampusInfo(record),
    },
    {
      title: t('event.education.column5'),
      dataIndex: 'quota',
      key: 'quota',
      align: 'center',
      render: (value) =>
        value === 0 ? t('event.education.text2') : `${t('event.education.text4')}${value?.toLocaleString()}${t('event.education.text5')}`,
    },
    {
      title: t('event.education.column6'),
      dataIndex: 'eventDate',
      key: 'eventDate',
      align: 'center',
      render: (value) => moment(value).format('YYYY.MM.DD'),
    },
    {
      title: t('event.education.column7'),
      dataIndex: 'registSrtDate',
      key: 'registSrtDate',
      align: 'center',
      render: (value) => moment(value).format('YYYY.MM.DD'),
    },
    {
      title: t('event.education.column8'),
      dataIndex: 'registEndDate',
      key: 'registEndDate',
      align: 'center',
      render: (value) => moment(value).format('YYYY.MM.DD'),
    },
  ];
  if (auth.hqYn === 'N') columns.splice(2, 1);

  return (
    <>
      <SimpleHeader
        iconFile="icon-event-big.svg"
        parentMenu={t('common.menu.event')}
        currentMenu={t('event.briefing.title1')}
        onlyTitle={true}
      />	  
      <div className="common-search">
        <div className="search-area">
          <ul className="search-bx">
            <li>
              <label>{t('common.menu.campus')}</label>
              <div>
                {auth.hqYn === 'Y' ? (
                  <SelectCampus
                    placeholder={t('common.menu.campus')}
                    value={campusCode}
                    onChange={(value: string) => setCampusCode(value)}
                  />
                ) : (
                  <SelectCampus />
                )}
              </div>
            </li>
            <li>
              <label>{t('event.education.column2')}</label>
              <input
                type="text"
                className="form-control w-150p"
                placeholder={t('event.education.placeholder1')}
                value={eventName}
                onChange={(e) => setEventName(e.target.value)}
                onKeyPress={(e) => {
                  if (e.key === 'Enter') onSearch();
                }}
              />
              <div className="comm-btn__area">
                <Button
                  className="btn-basic small black"
                  onClick={onSearch}
                  text={t('common.button.search')}
                />
              </div>
            </li>
          </ul>
        </div>
      </div>
    <div className="content scrollbar">
      {isLoading ? <Loader /> : null}
      <ErrorResponse error={error} />
      
      <div className="col-frame">
        <div className="comm-bx__between">
          <div className="comm-sch_result">{t('common.text.count')}: <b>{res?.total}</b>{t('common.text.unit')}</div>
          <div className="comm-btn__area">
            <Button
              className="btn-basic pink"
              onClick={() => history.push('/event/briefing/create')}
              text={t('event.education.button1')}
              disabled={campusDeactived}
              writeAuthYn={writeAuthYn}
            />
          </div>
        </div>
        <div className='comm-table__area'>
          <Table
            columns={columns}
            dataSource={res?.data}
            pagination={{
              current: page,
              pageSize: config.pageSize,
            }}
            onRow={(record) => {
              return {
                onClick: () => {
                  history.push(
                    `/event/briefing/${record.eventNo}${location.search}`,
                  );
                },
              };
            }}
            rowClassName="pointer"
            rowKey="eventNo"
          />
        </div>
        <div className="comm-bx__between">
          <div />
          <div className="common-paging center">
            <Pagination
              count={res?.total || 0}
              page={page}
              param={param}
              path="/event/briefing"
            />
          </div>
          <div className='comm-btn__area right'>
            <Button
              className="btn-basic pink"
              onClick={() => history.push('/event/briefing/create')}
              text={t('event.education.button1')}
              disabled={campusDeactived}
              writeAuthYn={writeAuthYn}
            />
          </div>
        </div>
      </div>
    </div>
    </>
  );
};

export default Briefing;
